import axios from '../helpers/axios'
import { logError } from '../services';
import {
  IResponse,
  IChecklistReviewJob,
  IPlaceOrderChecklistDetails,
  IPlaceOrderChecklistDetailsUpdate,
  IPlaceOrderChecklistTerritory,
  IChecklistReview,
  IDropdownResponse,
  IPlaceOrderChecklist
} from '../models';

export const getPlaceOrderChecklistTerritories = async (): Promise<
  IPlaceOrderChecklistTerritory[]
> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/territories`);
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistTerritories failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderChecklistJobs = async (territoryId: number | string): Promise<any> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/territories/${territoryId}/jobs`,
      { headers: { 'Content-Type': 'application/json' } }
    );
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistJobs failed');
    return error.response.data;
  }
};

export const getPlaceOrderChecklistStatuses = async (): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/statuses`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistStatuses failed');
    return error.response.data;
  }
};

export const getPlaceOrderChecklists = async (filters: {
  territoryId: string | number;
  jobSageId: string | number;
  orderNumber?: string | number;
  sageVendorId?: string | number;
  sageOrderTypeId?: string | number;
  status?: string;
  sortBy?: string;
  sortDirection?: string;
  isOpenEnded?: boolean;
}): Promise<IPlaceOrderChecklist[]> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/territories/${filters.territoryId}/jobs/${filters.jobSageId}/checklists`,
      { params: filters }
    );
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklists failed');
    return error.response.data;
  }
};

export const getPlaceOrderChecklistDetails = async (
  orderId: number | string,
  shouldShowAll?: boolean
): Promise<IPlaceOrderChecklistDetails> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/${orderId}${shouldShowAll ? `?ShowAll=${shouldShowAll}` : ''}`
    );
    return data;
  } catch (error: any) {
    logError(error, 'getPlaceOrderChecklistDetails failed');
    return Promise.reject(error.response.data);
  }
};

export const getPlaceOrderChecklistDetailsPreview = async (
  orderId: number | string,
  shouldShowAll?: boolean
): Promise<IPlaceOrderChecklistDetails> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/${orderId}/preview`,
      {
        params: {
          showAll: shouldShowAll
        }
      }
    );
    return data;
  } catch (error: any) {
    logError(error, 'getPlaceOrderChecklistDetailsPreview failed');
    return Promise.reject(error.response.data);
  }
};

export const updateChecklist = async (
  purchaseOrderId: number,
  checklist: IPlaceOrderChecklistDetailsUpdate
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/purchase-order-checklists/${purchaseOrderId}`,
      checklist
    );
    return data;
  } catch (error) {
    logError({ error, payload: { purchaseOrderId, checklist } }, 'updateChecklist failed');
    return Promise.reject(error?.response?.data);
  }
};

export const completeChecklist = async (
  purchaseOrderId: number,
  checklist: IPlaceOrderChecklistDetailsUpdate
): Promise<any> => {
  try {
    const { data } = await axios.post(`/api/purchase-order-checklists/${purchaseOrderId}/complete`);
    return data;
  } catch (error) {
    logError({ error, payload: { purchaseOrderId, checklist } }, 'completeChecklist failed');
    return Promise.reject(error?.response?.data);
  }
};

export const createWorkItemImage = async (
  workItemId: number,
  imagePost: FormData
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/purchase-order-checklists/work-items/${workItemId}/image`,
      imagePost
    );
    return data;
  } catch (error) {
    logError({ error, payload: { workItemId, imagePost } }, 'addWorkItemImage failed');
    return Promise.reject(error?.response?.data);
  }
};

export const getWorkItemImage = async (workItemId: number): Promise<any> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/work-items/${workItemId}/image`
    );
    return data;
  } catch (error) {
    logError({ error, payload: { workItemId } }, 'getWorkItemImage failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderChecklistReviews = async (filters?: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  isOpenEnded?: boolean;
  jobSageId?: string | number;
  completedOnStartDate?: string;
  completedOnEndDate?: string;
}): Promise<IResponse<IChecklistReview[]>> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/reviews`, { params: filters });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistTerritories failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderChecklistReviewsJobs = async (filters?: {
  isOpenEnded?: boolean | null;
}): Promise<IChecklistReviewJob[]> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/reviews/jobs`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistTerritories failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderChecklistReviewsArchive = async (filters?: {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDirection?: string;
  jobSageId?: string | number;
  completedOnStartDate?: string;
  completedOnEndDate?: string;
}): Promise<IResponse<IChecklistReview[]>> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/reviews/archive`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistTerritories failed');
    return Promise.reject(error);
  }
};

export const getPlaceOrderChecklistReviewsArchiveJobs = async (): Promise<
  IChecklistReviewJob[]
> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/reviews/archive/jobs`);
    return data;
  } catch (error) {
    logError(error, 'getPlaceOrderChecklistTerritories failed');
    return Promise.reject(error);
  }
};

export const createDeficiencyReport = async (
  purchaseOrderChecklistId: number | string
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/purchase-order-checklists/purchase-order/${purchaseOrderChecklistId}/deficiency-report`
    );
    return data;
  } catch (error: any) {
    logError({ error, payload: { purchaseOrderChecklistId } }, 'getDeficiencyReport failed');
    return error.response.data;
  }
};

export const getDeficiencyReport = async (
  purchaseOrderChecklistId: number | string
): Promise<any> => {
  try {
    const { data } = await axios.get(
      `/api/purchase-order-checklists/purchase-order/${purchaseOrderChecklistId}/deficiency-report`,
      {
        responseType: 'blob'
      }
    );
    return data;
  } catch (error: any) {
    logError({ error, payload: { purchaseOrderChecklistId } }, 'getDeficiencyReport failed');
    return error.response.data;
  }
};

export const checklistRequestRecheck = async (
  purchaseOrderChecklistId: number | string
): Promise<any> => {
  try {
    const { data } = await axios.put(
      `/api/purchase-order-checklists/${purchaseOrderChecklistId}/request-recheck`
    );
    return data;
  } catch (error: any) {
    logError({ error, payload: { purchaseOrderChecklistId } }, 'getDeficiencyReport failed');
    return error.response.data;
  }
};

export const processPurchaseOrderChecklist = async (
  purchaseOrderCheckListIds: any
): Promise<any> => {
  try {
    const { data } = await axios.post(
      `/api/purchase-order-checklists/process`,
      purchaseOrderCheckListIds
    );
    return data;
  } catch (error) {
    logError(
      { error, payload: { purchaseOrderCheckListIds } },
      'processPurchaseOrderChecklist failed'
    );
    return Promise.reject(error.response.data);
  }
};

export const getAllPurchaseOrderChecklists = async (filters?: {
  isOpenEnded?: boolean;
  jobSageId?: string | number;
  completedOnStartDate?: string;
  completedOnEndDate?: string;
}): Promise<IResponse<any>> => {
  try {
    const { data } = await axios.get(`/api/purchase-order-checklists/reviews/processable`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getAllPurchaseOrderChecklists failed');
    return Promise.reject(error);
  }
};
